import type { ZoomMeetingSummary } from "../types";

// One switchable view of a meeting summary. `body` is markdown, rendered by the
// same <Markdown> the panel already used for summary_content. `link` is set when
// a saved summary is only a link to a document elsewhere.
export type SummaryEntry = {
  key: string;
  label: string;
  body: string;
  link?: string;
};

/**
 * The document a saved summary points to, when the summary is nothing but a link.
 *
 * Bill writes his summaries in ChatGPT, saves them as a PDF in Dropbox and pastes
 * the Dropbox link as the whole summary (meetings 460 and 468; 455 holds a
 * chatgpt.com share link). Such a summary should open the document, not show a
 * URL. Only a lone http(s) URL counts: text that mentions a link is a summary,
 * and a URL without a scheme stays text rather than being guessed at. LCCRM-88.
 */
export function summaryLink(body: string | null | undefined): string | null {
  let text = (body ?? "").trim();
  // Pasting a link copied from a web page arrives as markdown, not a bare URL:
  // usePasteAsMarkdown turns the clipboard's <a> into [text](url) (meeting 412
  // holds a Plane link that way). A body that is exactly one such link, or an
  // <url> autolink, is still just a link.
  const wrapped =
    text.match(/^\[[^\]]*\]\((\S+)\)$/) ?? text.match(/^<(\S+)>$/);
  if (wrapped) text = wrapped[1];
  if (text === "" || /\s/.test(text)) return null;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  return url.protocol === "https:" || url.protocol === "http:" ? text : null;
}

// The record fields these entries are built from. Narrowed so tests do not have
// to construct a whole meeting.
export type SummarySource = Pick<
  ZoomMeetingSummary,
  "summary_overview" | "summary_details"
>;

/**
 * The summary views a meeting can offer, in display order.
 *
 * Zoom splits a summary across three fields and sends whichever ones it
 * produced. summary_content holds the next steps and is the only one the review
 * page edits; the other two are read-only narrative.
 *
 * An entry with nothing in it is not offered. A meeting from before the
 * narrative columns existed therefore yields a single entry, which is what keeps
 * the panel looking exactly as it did: the caller only draws the selector when
 * there is more than one.
 */
// A summary someone wrote themselves, most often pasted back from a chat
// assistant. Reused from the record type so the two cannot drift.
export type CustomSummary = NonNullable<
  ZoomMeetingSummary["custom_summaries"]
>[number];

export function summaryEntries(
  record: SummarySource | undefined,
  summaryContent: string | null | undefined,
  custom?: CustomSummary[] | null,
): SummaryEntry[] {
  const entries: SummaryEntry[] = [];

  const overview = record?.summary_overview?.trim();
  if (overview) {
    entries.push({ key: "recap", label: "Recap", body: overview });
  }

  const details = record?.summary_details ?? [];
  if (details.length > 0) {
    entries.push({
      key: "topics",
      label: "By topic",
      // Joined into markdown rather than rendered as its own component, so the
      // section titles become headings and nothing new has to be styled.
      body: details.map((d) => `### ${d.label}\n\n${d.summary}`).join("\n\n"),
    });
  }

  const content = summaryContent?.trim();
  if (content) {
    entries.push({ key: "next", label: "Next steps", body: content });
  }

  // Listed even with an empty body, which is the opposite rule to Zoom's above.
  // An absent recap should not be offered; a summary the user created that
  // disappeared because they had not typed into it yet would read as data loss.
  for (const c of custom ?? []) {
    const body = c.body ?? "";
    const link = summaryLink(body);
    entries.push({
      key: c.id,
      label: c.label,
      body,
      ...(link ? { link } : {}),
    });
  }

  return entries;
}
